import { Box, Chip } from '@mui/material';
import { MarkdownField } from '@react-admin/ra-markdown';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import isFunction from 'lodash/isFunction';
import {
  ListContextProvider,
  useListContext,
  useListController,
  useRecordContext,
} from 'react-admin';

import { GetCommentOneResponse } from '@Common/types/bo/comments';

import { CommentsProps } from '@sh4res/bo-common/components/Comments/CommentsButton';
import { CommentsCreate } from '@sh4res/bo-common/components/Comments/CommentsCreate';
import { useCanAccess } from '@sh4res/bo-common/hooks';

export function CommentsPanel({ source, target, getFilterValue }: CommentsProps) {
  const record = useRecordContext();

  const showCreateComment = useCanAccess({
    action: 'create',
    resource: 'bo_comments',
  });

  const effectiveTarget = isFunction(target) ? target(record) : target;

  const filteringValue = getFilterValue ? getFilterValue(record?.[source]) : record?.[source];

  const listContext = useListController<GetCommentOneResponse>({
    resource: 'bo_comments',
    filter: filteringValue !== undefined ? { [effectiveTarget as string]: filteringValue } : {},
    sort: { field: 'created_at', order: 'DESC' },
    perPage: 50,
  });

  return (
    <Box>
      <ListContextProvider value={listContext}>
        <Box data-testid="BoExternalCommentsPanel_CommentsList">
          <CommentsList hasCreateComment={showCreateComment} />
        </Box>
      </ListContextProvider>
      <Box flex={1} />
      {showCreateComment && (
        <CommentsCreate target={effectiveTarget} source={source} getFilterValue={getFilterValue} />
      )}
    </Box>
  );
}

export function CommentsList({ hasCreateComment }: { hasCreateComment: boolean }) {
  const { data = [] } = useListContext<GetCommentOneResponse>();

  return (
    <Box sx={{ overflowY: 'auto', height: hasCreateComment ? 'calc(100vh - 385px)' : undefined }}>
      {data.map((record) => (
        <Box
          data-testid="BoExternalCommentsPanel_CommentField"
          sx={(theme) => ({
            display: 'flex',
            flexDirection: 'column',
            rowGap: '5px',
            wordBreak: 'break-word',
            paddingLeft: '25px',
            paddingTop: '7px',
            paddingBottom: '7px',
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          })}
          key={record.id}>
          <Box
            sx={{
              color: '#999999',
              fontSize: 'small',
            }}>
            {`${format(
              toZonedTime(new Date(record.created_at), 'UTC'),
              'd MMM, yyyy HH:mm:ss',
            )} UTC`}
          </Box>
          <Chip sx={{ maxWidth: 'fit-content' }} label={record.bo_google_user_email} />
          <MarkdownField
            data-testid="BoExternalCommentsPanel_CommentMarkdownField"
            record={record}
            source="comment"
            linkAttributes={{ target: '_blank', rel: 'noopener noreferrer' }}
            extendedAutolinks={true}
            label={false}
            sx={{
              '& .toastui-editor-contents': {
                overflowWrap: 'anywhere !important',
                '& p': { margin: 0 },
              },
            }}
          />
        </Box>
      ))}
    </Box>
  );
}
